// src/routes/StagingAdminGate.jsx
import { useRef } from "react";
import { Outlet } from "react-router-dom";
import { Alert, AlertTitle, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useCurrentUserAccess } from "../hooks/useCurrentUserAccess";

// Only active on the staging build (REACT_APP_ENV=staging). In production this
// is false, so the gate is a no-op and all authenticated users pass through.
const IS_STAGING = process.env.REACT_APP_ENV === "staging";

export default function StagingAdminGate() {
  const { accessLoading, isAdminUser } = useCurrentUserAccess();
  // Latch: once access has been granted we stop re-evaluating. accessLoading
  // includes demoLoading, which flips true on every background demo/quota
  // refresh (e.g. Simulation & Reports refreshing counters on mount). Without
  // this latch the whole app would swap back to the "Checking staging access…"
  // screen on each such refresh, which looks like the page reloading.
  const hasPassedRef = useRef(false);

  // Not staging → behave exactly as before.
  if (!IS_STAGING) {
    return <Outlet />;
  }

  // Already granted once — ignore transient accessLoading toggles.
  if (hasPassedRef.current) {
    return <Outlet />;
  }

  if (accessLoading) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Checking staging access…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!isAdminUser) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <AlertTitle>Staging environment</AlertTitle>
          This is the staging (test) environment and is restricted to administrators.
          Please use the production platform.
        </Alert>
      </Box>
    );
  }

  // Access confirmed — latch so later background refreshes don't re-gate.
  hasPassedRef.current = true;
  return <Outlet />;
}